// src/components/test/QuestionDisplay.tsx
import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Question } from '../../types/testData';

// Базовый кегль стимула, когда явный кегль (font_size) в ЭТЗ не задан.
const DEFAULT_STIMULUS_FONT_SIZE = '3.8rem';
const DEFAULT_STIMULUS_FONT_SIZE_MOBILE = '2.85rem';

// Единый базовый кегль для режима «Цветомер»: одинаков для всех стимулов,
// чтобы переход между короткими и длинными стимулами не «прыгал».
const COLOR_MODE_BASE_PX = 2.2 * 16;       // ≈35 px (десктоп)
const COLOR_MODE_BASE_PX_MOBILE = 1.8 * 16; // ≈29 px (узкие экраны)
const MOBILE_BREAKPOINT = 480;

// Минимальный кегль, ниже которого не опускаемся (чтобы текст оставался читаемым).
const MIN_FONT_PX = 12;
// Для «Цветомера» — чуть выше: даже длинные стимулы должны оставаться читаемыми.
const MIN_FONT_PX_COLOR = 16;

// 1pt ≈ 1.333px (96/72 dpi).
const PT_TO_PX = 96 / 72;
const REM_TO_PX = 16;

// Ширина самого длинного слова в долях от кегля (измеряется скрытым span,
// который рендерится тем же шрифтом, что и заголовок — совпадает с реальным браузером).
function maxWordWidthFactor(content: string, fontFamily: string, fontWeight: string, container: HTMLElement): number {
  const words = (content || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 0;
  const span = document.createElement('span');
  span.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font-family:${fontFamily};font-weight:${fontWeight};font-size:100px;`;
  container.appendChild(span);
  let factor = 0;
  for (const w of words) {
    span.textContent = w;
    factor = Math.max(factor, span.getBoundingClientRect().width / 100);
  }
  span.remove();
  return factor;
}

// Максимальный «задуманный» кегль (в px) для стимула до подгонки под ширину.
function maxFontPx(colorMode: boolean, fontPt: number | null | undefined, width: number): number {
  if (colorMode) {
    return width <= MOBILE_BREAKPOINT ? COLOR_MODE_BASE_PX_MOBILE : COLOR_MODE_BASE_PX;
  }
  if (fontPt && fontPt > 0) return fontPt * PT_TO_PX;
  // Внешний CSS-медиазапрос применит DEFAULT_STIMULUS_FONT_SIZE_MOBILE на узких экранах;
  // здесь используем десктопный базовый кегль как верхнюю границу.
  return 3.8 * REM_TO_PX;
}

// Высота текста при заданном кегле и ширине (как он реально сверстается).
function textBlockHeight(
  content: string,
  fontFamily: string,
  fontWeight: string,
  width: number,
  fontSize: number,
  container: HTMLElement
): number {
  const span = document.createElement('span');
  span.style.cssText =
    `position:absolute;visibility:hidden;left:-9999px;top:0;width:${width}px;` +
    `font-family:${fontFamily};font-weight:${fontWeight};font-size:${fontSize}px;` +
    `line-height:1.5;white-space:normal;overflow-wrap:break-word;word-break:break-word;text-align:center;`;
  span.textContent = content;
  container.appendChild(span);
  const height = span.getBoundingClientRect().height;
  span.remove();
  return height;
}

// Умещается ли текст в две строки при этом кегле (и без переноса по буквам).
function fitsInTwoLines(
  content: string,
  fontFamily: string,
  fontWeight: string,
  usableWidth: number,
  wordFactor: number,
  fontSize: number,
  container: HTMLElement
): boolean {
  if (wordFactor * fontSize > usableWidth + 0.5) return false;
  // line-height 1.5: две строки = 3 * fontSize.
  return textBlockHeight(content, fontFamily, fontWeight, usableWidth, fontSize, container) <= fontSize * 3 + 1;
}

// Подбирает наибольший кегль ≤ maxPx, при котором текст помещается в две строки.
function fitToTwoLines(
  content: string,
  fontFamily: string,
  fontWeight: string,
  usableWidth: number,
  wordFactor: number,
  maxPx: number,
  container: HTMLElement
): number {
  const fits = (size: number) =>
    fitsInTwoLines(content, fontFamily, fontWeight, usableWidth, wordFactor, size, container);
  if (fits(maxPx)) return maxPx;
  if (!fits(MIN_FONT_PX_COLOR)) return MIN_FONT_PX_COLOR;
  let lo = MIN_FONT_PX_COLOR;
  let hi = maxPx;
  while (hi - lo > 0.5) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) hi = mid;
    else lo = mid;
  }
  return hi;
}

const QuestionDisplayContainer = styled.div<{ $stableHeight?: boolean; $lineHeightPx?: number }>`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* Резервируем высоту окна стимула под 3 строки фактического кегля (line-height 1.5).
     Окно стабильно при 1/2/3 строках и текст центрирован по вертикали; если строк
     больше 3 — контейнер растёт (min-height), а не «прыгает». Для «Цветомера» (ЧОУ-2)
     оставляем 2 строки. align-items не задаём (stretch), чтобы text-align: justify
     (justify_text) продолжал работать на всю ширину. */
  min-height: ${({ $lineHeightPx }) => `${(($lineHeightPx ?? 3.8 * REM_TO_PX) * 3).toFixed(1)}px`};
  ${({ $stableHeight }) =>
    $stableHeight &&
    `
    min-height: ${(2 * 1.5 * COLOR_MODE_BASE_PX).toFixed(1)}px;
    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      min-height: ${(2 * 1.5 * COLOR_MODE_BASE_PX_MOBILE).toFixed(1)}px;
    }
  `}
`;

const QuestionTitle = styled.h2<{ $customFontSize?: string; $justify?: boolean }>`
  font-size: ${({ $customFontSize }) => $customFontSize ?? DEFAULT_STIMULUS_FONT_SIZE};
  color: #586266;
  line-height: 1.5;
  font-weight: 700;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: ${({ $justify }) => ($justify ? 'justify' : 'center')};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ $customFontSize }) =>
      $customFontSize ?? DEFAULT_STIMULUS_FONT_SIZE_MOBILE};
  }
`;

const MediaContainer = styled.div`
  margin: ${({ theme }) => theme.spacing.lg} 0;
  text-align: center;

  img,
  video {
    max-width: 100%;
    max-height: 400px;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

interface QuestionDisplayProps {
  question: Question;
  colorMode?: boolean;
}

export const QuestionDisplay: React.FC<QuestionDisplayProps> = ({ question, colorMode = false }) => {
  const justifyText = question.parameters?.justify_text === 'true';
  const containerRef = useRef<HTMLDivElement>(null);
  const [fitSizePx, setFitSizePx] = useState<number | null>(null);

  // Подбираем кегль: для «Цветомера» — единый базовый, уменьшаем только если
  // стимул не умещается в две строки (или длинное слово уже карточки);
  // для остальных режимов — как раньше (по ширине самого длинного слова).
  // useLayoutEffect (а не useEffect): измеряем и пересчитываем кегль до отрисовки
  // браузером, чтобы длинное слово не «выпадало» за край и не анимировалось.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !question.content?.trim()) {
      setFitSizePx(null);
      return;
    }
    const compute = () => {
      const width = el.clientWidth;
      if (!width) return;
      const maxPx = maxFontPx(colorMode, question.font_size, width);
      const style = window.getComputedStyle(el);
      const fontFamily = style.fontFamily || 'sans-serif';
      const fontWeight = style.fontWeight || '700';
      const usableWidth = width - 24;
      const factor = maxWordWidthFactor(question.content, fontFamily, fontWeight, el);
      if (!factor) return;
      if (colorMode) {
        const fit = fitToTwoLines(question.content, fontFamily, fontWeight, usableWidth, factor, maxPx, el);
        setFitSizePx(Math.round(fit * 100) / 100);
        return;
      }
      const fit = Math.max(MIN_FONT_PX, Math.min(maxPx, usableWidth / factor));
      setFitSizePx(Math.round(fit * 100) / 100);
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, [question.id, question.content, question.font_size, colorMode]);

  const displayWidth =
    containerRef.current?.clientWidth ?? (typeof window !== 'undefined' ? window.innerWidth : MOBILE_BREAKPOINT + 1);
  const fontPx = fitSizePx ?? maxFontPx(colorMode, question.font_size, displayWidth);
  // Опорная высота строки — от фактического кегля стимула (без применения мин. подгонки):
  // окно резервирует 3 строки и не «распухает» до дефолтного кегля, если стимул мелкий.
  const linePx = (maxFontPx(colorMode, question.font_size, displayWidth) * 1.5) || 3.8 * REM_TO_PX * 1.5;
  return (
    <QuestionDisplayContainer ref={containerRef} $stableHeight={colorMode} $lineHeightPx={linePx}>
      <QuestionTitle
        $customFontSize={`${fontPx}px`}
        $justify={justifyText}
      >
        {question.content}
      </QuestionTitle>

      {question.type === 'media' && question.media_url && (
        <MediaContainer>
          {question.media_url.endsWith('.mp4') ||
          question.media_url.endsWith('.webm') ||
          question.media_url.endsWith('.ogg') ? (
            <video controls>
              <source src={question.media_url} type="video/mp4" />
              Ваш браузер не поддерживает видео
            </video>
          ) : (
            <img
              src={question.media_url}
              alt="Иллюстрация к стимулу"
              loading="lazy"
            />
          )}
        </MediaContainer>
      )}
    </QuestionDisplayContainer>
  );
};